import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ArrowSquareOut as ArrowSquareOutIcon } from "@phosphor-icons/react/ArrowSquareOut";

import { ResponsiveModal } from "../responsive-modal";

export interface V4ReleaseModalProps {
	/** Modal heading, e.g. "MangoMap v4 is here" */
	headline: string;
	/** Short body copy; a node so consumers can pass rich text */
	description: React.ReactNode;
	/** Primary link target, opened in a new tab */
	linkHref: string;
	/** Primary link text, e.g. "See what's new" */
	linkLabel: string;
	onClose: () => void;
	/** Optional telemetry hook; does not block navigation or dismiss the modal */
	onLinkClick?: () => void;
	/** Optional decorative graphic, rendered full-bleed below the title */
	headerGraphic?: React.ReactNode;
}

/**
 * Presentational v4 announcement modal. Renders the panel only — when it
 * appears and how its interactions persist are the consumer's responsibility.
 */
export function V4ReleaseModal({
	headline,
	description,
	linkHref,
	linkLabel,
	onClose,
	onLinkClick,
	headerGraphic,
}: V4ReleaseModalProps) {
	return (
		<ResponsiveModal
			actions={
				<Stack
					direction={{ xs: "column", sm: "row" }}
					spacing={2}
					sx={{
						alignItems: { xs: "flex-start", sm: "center" },
						justifyContent: "flex-end",
					}}>
					<Button
						component="a"
						endIcon={<ArrowSquareOutIcon />}
						href={linkHref}
						onClick={onLinkClick}
						rel="noopener noreferrer"
						target="_blank"
						variant="contained">
						{linkLabel}
					</Button>
				</Stack>
			}
			actionsPosition="content"
			onClose={onClose}
			title={headline}>
			{headerGraphic && (
				<Box
					sx={{
						mb: 3,
						lineHeight: 0,
						overflow: "hidden",
						borderRadius: 1,
						border: "1px solid var(--mui-palette-divider)",
					}}>
					{headerGraphic}
				</Box>
			)}
			<Typography
				color="text.secondary"
				component="div"
				variant="body1">
				{description}
			</Typography>
		</ResponsiveModal>
	);
}
